/**
 * FileUploader Component
 * Drag-and-drop file upload with format validation
 */

import React, { useState, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Upload, 
  File, 
  FileText, 
  FileSpreadsheet, 
  X, 
  CheckCircle2, 
  AlertCircle,
  Cloud,
  Loader2,
} from 'lucide-react';
import { SUPPORTED_FILE_FORMATS } from '@/constants/beStudyMockData';

/**
 * Get file icon based on extension
 */
const getFileIcon = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return FileText;
    case 'doc':
    case 'docx':
      return FileText;
    case 'xls':
    case 'xlsx':
      return FileSpreadsheet;
    default:
      return File;
  }
};

/**
 * Format file size
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Validate file
 */
const validateFile = (file, acceptedFormats, maxSize) => {
  const errors = [];
  
  // Check file size
  if (maxSize && file.size > maxSize * 1024 * 1024) {
    errors.push(`File size exceeds ${maxSize}MB limit`);
  }
  
  // Check file format
  if (acceptedFormats && acceptedFormats.length > 0) {
    const ext = file.name.split('.').pop()?.toLowerCase();
    const isAccepted = acceptedFormats.some(format => {
      if (format.startsWith('.')) {
        return format.slice(1).toLowerCase() === ext;
      }
      return format.toLowerCase() === ext;
    });
    
    if (!isAccepted) {
      errors.push(`File type .${ext} is not supported`);
    }
  }
  
  return errors;
};

/**
 * Single file item display
 */
function FileItem({ file, onRemove, status = 'idle', progress = 0, error }) {
  const FileIcon = getFileIcon(file.name);
  
  return (
    <div 
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg border bg-card',
        error && 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/20',
        status === 'success' && 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/20'
      )}
      data-testid="file-item"
    >
      <div className={cn(
        'flex items-center justify-center w-10 h-10 rounded-lg',
        error ? 'bg-red-100 dark:bg-red-900/30' : 'bg-muted'
      )}>
        <FileIcon className={cn('h-5 w-5', error ? 'text-red-500' : 'text-muted-foreground')} />
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
        
        {status === 'uploading' && (
          <div className="mt-1.5">
            <Progress value={progress} className="h-1" />
          </div>
        )}
        
        {error && (
          <p className="text-xs text-red-500 mt-1">{error}</p>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        {status === 'uploading' && (
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        )}
        {status === 'success' && (
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        )}
        {status === 'error' && (
          <AlertCircle className="h-4 w-4 text-red-500" />
        )}
        
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={() => onRemove(file)}
            data-testid="remove-file-button"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Main FileUploader component
 */
export function FileUploader({
  onUpload,
  onRemove,
  accept,
  maxSize = 50, // MB
  maxFiles = 10,
  multiple = false,
  disabled = false,
  files = [],
  uploadProgress = {},
  uploadStatus = {},
  uploadErrors = {},
  className,
  dropzoneText = 'Drag and drop files here, or click to browse',
  supportedFormatsText,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  // Default accepted formats
  const acceptedFormats = accept || SUPPORTED_FILE_FORMATS.map(f => f.extension);
  const acceptString = acceptedFormats.join(',');

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, [disabled]);

  const handleFileInput = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files || []);
    handleFiles(selectedFiles);
    // Reset input so same file can be selected again
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const handleFiles = useCallback((newFiles) => {
    // Check max files limit
    const availableSlots = maxFiles - files.length;
    const filesToProcess = multiple ? newFiles.slice(0, availableSlots) : [newFiles[0]];

    // Validate and process each file
    const validFiles = [];
    const errors = {};

    filesToProcess.forEach((file) => {
      const fileErrors = validateFile(file, acceptedFormats, maxSize);
      if (fileErrors.length === 0) {
        validFiles.push(file);
      } else {
        errors[file.name] = fileErrors.join(', ');
      }
    });

    if (validFiles.length > 0 && onUpload) {
      onUpload(multiple ? validFiles : validFiles[0], errors);
    }
  }, [files.length, maxFiles, multiple, acceptedFormats, maxSize, onUpload]);

  const handleClick = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  }, [disabled]);

  const handleRemove = useCallback((file) => {
    if (onRemove) {
      onRemove(file);
    }
  }, [onRemove]);

  // Format text for supported formats
  const formatsText = supportedFormatsText || 
    `Supported: ${SUPPORTED_FILE_FORMATS.slice(0, 4).map(f => f.extension).join(', ')}`;

  return (
    <div className={cn('w-full', className)} data-testid="file-uploader">
      {/* Dropzone */}
      <div
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
          isDragging && 'border-primary bg-primary/5',
          !isDragging && !disabled && 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50',
          disabled && 'opacity-50 cursor-not-allowed bg-muted'
        )}
        data-testid="dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={acceptString}
          multiple={multiple}
          onChange={handleFileInput}
          disabled={disabled}
          data-testid="file-input"
        />

        <div className="flex flex-col items-center gap-3">
          <div className={cn(
            'w-14 h-14 rounded-full flex items-center justify-center',
            isDragging ? 'bg-primary/10' : 'bg-muted'
          )}>
            {isDragging ? (
              <Cloud className="h-7 w-7 text-primary" />
            ) : (
              <Upload className="h-7 w-7 text-muted-foreground" />
            )}
          </div>

          <div>
            <p className="text-sm font-medium">
              {isDragging ? 'Drop files here' : dropzoneText}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatsText} (Max: {maxSize}MB)
            </p>
          </div>

          {!disabled && (
            <Button type="button" variant="secondary" size="sm" className="mt-2">
              Browse Files
            </Button>
          )}
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="file-list">
          {files.map((file, index) => (
            <FileItem
              key={file.name + index}
              file={file}
              onRemove={handleRemove}
              status={uploadStatus[file.name] || 'idle'}
              progress={uploadProgress[file.name] || 0}
              error={uploadErrors[file.name]}
            />
          ))}
        </div>
      )}

      {/* File count indicator */}
      {multiple && maxFiles > 1 && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          {files.length} of {maxFiles} files
        </p>
      )}
    </div>
  );
}

/**
 * Compact single file uploader
 */
export function SingleFileUploader({
  onUpload,
  onRemove,
  accept,
  maxSize = 50,
  disabled = false,
  file,
  uploadProgress = 0,
  uploadStatus = 'idle',
  error,
  label,
  className,
}) {
  const inputRef = useRef(null);
  const acceptedFormats = accept || SUPPORTED_FILE_FORMATS.map(f => f.extension);
  const acceptString = acceptedFormats.join(',');

  const handleFileInput = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && onUpload) {
      const errors = validateFile(selectedFile, acceptedFormats, maxSize);
      onUpload(selectedFile, errors.length > 0 ? errors.join(', ') : null);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={cn('w-full', className)} data-testid="single-file-uploader">
      {label && (
        <label className="text-sm font-medium mb-2 block">{label}</label>
      )}
      
      {!file ? (
        <div
          onClick={() => !disabled && inputRef.current?.click()}
          className={cn(
            'flex items-center gap-3 p-3 border border-dashed rounded-lg cursor-pointer transition-colors',
            !disabled && 'hover:border-primary/50 hover:bg-muted/50',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
            <Upload className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <p className="text-sm">Click to upload</p>
            <p className="text-xs text-muted-foreground">Max {maxSize}MB</p>
          </div>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept={acceptString}
            onChange={handleFileInput}
            disabled={disabled}
          />
        </div>
      ) : (
        <FileItem
          file={file}
          onRemove={onRemove}
          status={uploadStatus}
          progress={uploadProgress}
          error={error}
        />
      )}
    </div>
  );
}

export default FileUploader;
